import { parseQuery } from "@plannello/core/query/parse";
import { type ResolveContext, type ResolvedQuery, resolveQuery } from "@plannello/core/query/resolve";
import { compareItems, createPredicateContext, toPredicate } from "@plannello/core/query/toPredicate";
import type { ItemDetail, ItemLite, QueryAst, QuerySort, QueryWarning } from "@plannello/shared";
import { keepPreviousData, queryOptions, useQueries, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useMe } from "@/lib/queries";
import { useCatalog } from "./CatalogProvider";
import type { Catalog } from "./catalog";
import { itemKey, litesKey, searchKey } from "./store";
import { useExtraTitleText } from "./titles";
import type { SearchResult } from "./types";

/**
 * Item reads.
 *
 * Client vs server rule (B4 "sync-lite"):
 *   1. A view whose scope has at most CLIENT_SCOPE_LIMIT products loads every lite row of those
 *      products once (["lites", productId]) and evaluates the query in the browser with the same
 *      resolver the server uses (`resolveQuery` + `toPredicate` + `compareItems`). Filter, group,
 *      sort and layout switches therefore never wait for the network.
 *   2. Free text in such a query matches titles (the original and the translated one the reader sees) and
 *      keys client-side immediately (the server searches originals only); because the
 *      server also searches descriptions (full text), the same query is sent to
 *      GET /items?q= (debounced by the caller) and its hits are merged in ("hybrid").
 *   3. A scope with more products than the limit is answered by the server alone (first 500 rows,
 *      same order), so a large instance never downloads everything for one view.
 */
export const CLIENT_SCOPE_LIMIT = 12;
const SERVER_LIMIT = 500;

export const litesQuery = (productId: string) =>
  queryOptions({
    queryKey: litesKey(productId),
    queryFn: ({ signal }) => api.get<ItemLite[]>(`/api/v1/item-lites?products=${productId}`, signal),
    staleTime: 60_000,
    // The live stream catches up after a reconnect (replay, or resync). A refetch the moment the
    // network returns would race with queued offline writes that are being replayed.
    refetchOnReconnect: false,
  });

export const itemDetailQuery = (ref: string) =>
  queryOptions({
    queryKey: itemKey(ref.toUpperCase()),
    queryFn: ({ signal }) =>
      api.get<ItemDetail & { redirectedFrom: string | null }>(`/api/v1/items/${encodeURIComponent(ref)}`, signal),
    staleTime: 15_000,
  });

const NO_ITEMS: ItemLite[] = [];

export type ProductItems = {
  items: ItemLite[];
  byId: Map<string, ItemLite>;
  /** True until every requested product has loaded once (cold load). */
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
};

/** Lite rows of the given products, merged. Warm loads are synchronous (cache). */
export function useProductItems(productIds: readonly string[]): ProductItems {
  const r = useQueries({
    queries: productIds.map((id) => litesQuery(id)),
    combine: (results) => ({
      lists: results.map((x) => x.data ?? NO_ITEMS),
      stamp: results.map((x) => x.dataUpdatedAt).join(),
      isPending: results.some((x) => x.isPending),
      isError: results.some((x) => x.isError),
      refetch: () => {
        for (const x of results) if (x.isError) void x.refetch();
      },
    }),
  });
  // `stamp` alone is not an identity: two products' lists can share a timestamp (a move between
  // products writes both in the same millisecond), so switching scope must invalidate too.
  const scope = productIds.join();
  // biome-ignore lint/correctness/useExhaustiveDependencies: `scope` + `stamp` change whenever any list does
  return useMemo(() => {
    const items = r.lists.length === 1 ? r.lists[0]! : r.lists.flat();
    return {
      items,
      byId: new Map(items.map((i) => [i.id, i])),
      isPending: r.isPending,
      isError: r.isError,
      refetch: r.refetch,
    };
  }, [scope, r.stamp, r.isPending, r.isError]);
}

/** Everything `resolveQuery` needs, from the catalog and the cached rows. */
export function resolveContextFor(
  catalog: Catalog,
  userId: string | null,
  items: Iterable<ItemLite>,
  now = new Date(),
): ResolveContext {
  const byKey = new Map<string, string>();
  for (const i of items) byKey.set(i.key, i.id);
  return {
    types: catalog.types,
    statuses: [...catalog.statusById.values()],
    products: catalog.products.map((p) => ({ id: p.id, key: p.key, spaceId: p.spaceId })),
    spaces: catalog.spaces.map((s) => ({ id: s.id, key: s.key, name: s.name })),
    labelGroups: catalog.labelGroups,
    labels: catalog.labels.map((l) => ({
      ...l,
      groupSlug: l.groupId ? (catalog.groupById.get(l.groupId)?.slug ?? null) : null,
    })),
    milestones: catalog.milestones.map((ms) => ({
      id: ms.id,
      productId: ms.productId,
      name: ms.name,
      version: ms.version,
    })),
    users: catalog.users.map((u) => ({ id: u.id, name: u.name, email: u.email })),
    userId,
    now,
    itemIdByKey: (key) => byKey.get(key),
  };
}

export type ItemsMode = "client" | "hybrid" | "server";

export type ItemsResult = {
  items: ItemLite[];
  /** All cached rows of the scope (for parent chains, child counts, blocked state). */
  all: ProductItems;
  ast: QueryAst;
  resolved: ResolvedQuery | null;
  warnings: QueryWarning[];
  mode: ItemsMode;
  isPending: boolean;
  isError: boolean;
  /** Server part of a hybrid query is still loading (client hits are already shown). */
  isSearching: boolean;
  refetch: () => void;
};

const hasFreeText = (r: ResolvedQuery) => r.conditions.some((c) => c.t === "text" && !c.negate);

/**
 * Runs a query-language string over a scope of products (see the rule above). `sort` overrides
 * the query's own `sort:` clauses (view display options); without either, items keep manual rank.
 */
export function useItems(
  queryText: string,
  scopeProductIds: readonly string[],
  opts: { sort?: QuerySort[]; serverQueryText?: string } = {},
): ItemsResult {
  const catalog = useCatalog();
  const me = useMe().data;
  const extraText = useExtraTitleText();
  const clientScope = scopeProductIds.length <= CLIENT_SCOPE_LIMIT;
  const all = useProductItems(clientScope ? scopeProductIds : []);

  const ast = useMemo(() => parseQuery(queryText), [queryText]);
  const resolved = useMemo(() => {
    if (!catalog.ready) return null;
    const astWithSort = opts.sort && opts.sort.length > 0 ? { ...ast, sort: opts.sort } : ast;
    return resolveQuery(astWithSort, resolveContextFor(catalog, me?.id ?? null, all.items));
  }, [ast, catalog, me?.id, all.items, opts.sort]);

  const needsServer = !clientScope || (resolved ? hasFreeText(resolved) : false);
  const scopeKeys = useMemo(
    () =>
      scopeProductIds
        .map((id) => catalog.productById.get(id)?.key)
        .filter(Boolean)
        .join(","),
    [scopeProductIds, catalog],
  );
  // Typing must not fire a full-text request per keystroke: the server part waits for a pause.
  const liveServerText = `${opts.serverQueryText ?? queryText}${scopeKeys ? ` product:${scopeKeys}` : ""}`;
  const serverText = useDebounced(liveServerText, 250);
  const server = useQuery({
    queryKey: searchKey(serverText),
    queryFn: ({ signal }) =>
      api.get<SearchResult<ItemLite>>(
        `/api/v1/items?q=${encodeURIComponent(serverText)}&limit=${SERVER_LIMIT}`,
        signal,
      ),
    enabled: needsServer && catalog.ready && scopeProductIds.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });

  const items = useMemo(() => {
    if (!resolved) return NO_ITEMS;
    const cmp = compareItems(resolved.sort);
    if (!clientScope) return server.data?.items ?? NO_ITEMS;
    const pred = toPredicate(resolved, createPredicateContext(all.items, [...catalog.statusById.values()], extraText));
    const hits = all.items.filter(pred);
    // Only merge hits that belong to the text on screen (not a debounced or previous query).
    const fresh = server.data && !server.isPlaceholderData && serverText === liveServerText;
    if (needsServer && fresh && server.data) {
      const seen = new Set(hits.map((i) => i.id));
      for (const s of server.data.items) {
        if (seen.has(s.id)) continue;
        // Prefer the cached row (it carries optimistic edits) over the search snapshot.
        const cached = all.byId.get(s.id);
        if (cached && (cached.archivedAt !== null) !== (resolved.archived === "only")) continue;
        hits.push(cached ?? s);
      }
    }
    return hits.sort(cmp);
  }, [
    resolved,
    all,
    clientScope,
    needsServer,
    server.data,
    server.isPlaceholderData,
    serverText,
    liveServerText,
    catalog,
    extraText,
  ]);

  return {
    items,
    all,
    ast,
    resolved,
    warnings: resolved?.warnings ?? ast.warnings,
    mode: !clientScope ? "server" : needsServer ? "hybrid" : "client",
    isPending: !catalog.ready || (clientScope ? all.isPending : server.isPending),
    isError: clientScope ? all.isError : server.isError,
    isSearching: needsServer && (server.isFetching || serverText !== liveServerText),
    refetch: () => (clientScope ? all.refetch() : void server.refetch()),
  };
}

/** Full item (description, activity, comments, relations). `ref` is a key (any case) or an id. */
export function useItemDetail(ref: string) {
  return useQuery(itemDetailQuery(ref));
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
